'use client'

import { App, Button, Form, Spin } from 'antd'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { API_ERROR } from './contract'
import type { LoginApi } from './login.api'
import { PasswordWithConfirm } from './password.with.confirm'
import type { ChangePasswordTexts } from './texts'

/** Props of {@link ChangePasswordForm}. */
export interface ChangePasswordFormProps {
  api: LoginApi
  texts: ChangePasswordTexts
  /** URL path of the login page, opened after the change. */
  loginPath: string
  /** See {@link PasswordWithConfirm}. */
  minLength?: number
  /** Reports unexpected failures, e.g. to error tracking. */
  onError?: (error: unknown) => void
}

interface ChangePasswordValues {
  password: string
}

/**
 * Page that replaces an expired password. {@link LoginForm} opens it with the token of the
 * API's `PASSWORD_EXPIRED` error (`?token=`); afterwards the user signs in again.
 */
export function ChangePasswordForm({
  api,
  texts,
  loginPath,
  minLength,
  onError,
}: ChangePasswordFormProps) {
  const router = useRouter()
  const { message } = App.useApp()
  const token = useSearchParams().get('token') ?? ''
  const [submitting, setSubmitting] = useState(false)

  const change = async ({ password }: ChangePasswordValues) => {
    setSubmitting(true)
    const result = await api.changePassword({ password, token })
    setSubmitting(false)

    if (result.ok) {
      void message.success(texts.updated)
      router.push(loginPath)
    } else if (result.errors.some((e) => e.message === API_ERROR.passwordNotGoodEnough)) {
      void message.error(texts.notGoodEnough)
    } else {
      onError?.(result.errors)
      void message.error(texts.failed)
    }
  }

  return (
    <>
      <h1>{texts.title}</h1>
      <Spin spinning={submitting}>
        <Form<ChangePasswordValues> layout="vertical" onFinish={(values) => void change(values)}>
          <PasswordWithConfirm texts={texts.password} minLength={minLength} />
          <Form.Item>
            <Button size="large" type="primary" htmlType="submit" block>
              {texts.submit}
            </Button>
            <Link href={loginPath}>
              <Button size="large" type="link" block>
                {texts.backToLogin}
              </Button>
            </Link>
          </Form.Item>
        </Form>
      </Spin>
    </>
  )
}
